import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Drawer,
  CircularProgress,
  Toolbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArticleIcon from '@mui/icons-material/Article';
import PublishIcon from '@mui/icons-material/Publish';
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import { useSnackbar } from 'notistack';
import {
  useFetchBidRecommendationRunQuery,
  useReviewRecommendationMutation,
  useBulkReviewRecommendationsMutation,
  useLazyFetchRunReportQuery,
  usePublishRecommendationsMutation,
} from '../../slices/bidRecommendationSlice';
import {
  BidRecommendation,
  Action,
  Confidence,
  ReviewStatus,
} from '../../types/bidRecommendations';
import ReportDrawer from './ReportDrawer';
import RecommendationDetail from './RecommendationDetail';

interface RunDetailViewProps {
  runId: string;
  onBack: () => void;
}

const actionColors: Record<
  Action,
  'success' | 'error' | 'default' | 'warning'
> = {
  increase: 'success',
  decrease: 'error',
  hold: 'default',
  manual_review: 'warning',
};

const actionLabels: Record<Action, string> = {
  increase: 'Increase',
  decrease: 'Decrease',
  hold: 'Hold',
  manual_review: 'Manual Review',
};

const confidenceColors: Record<Confidence, 'success' | 'warning' | 'error'> = {
  high: 'success',
  medium: 'warning',
  low: 'error',
};

const reviewStatusColors: Record<
  ReviewStatus,
  'default' | 'success' | 'error'
> = {
  pending: 'default',
  accepted: 'success',
  rejected: 'error',
};

const ACTION_TABS = [
  'all',
  'increase',
  'decrease',
  'hold',
  'manual_review',
] as const;

const RunDetailView: React.FC<RunDetailViewProps> = ({ runId, onBack }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: run, isLoading } = useFetchBidRecommendationRunQuery(runId);
  const [reviewRecommendation] = useReviewRecommendationMutation();
  const [bulkReview] = useBulkReviewRecommendationsMutation();
  const [triggerReport, { data: reportData, isLoading: reportLoading }] =
    useLazyFetchRunReportQuery();
  const [publishRecommendations, { isLoading: isPublishing }] =
    usePublishRecommendationsMutation();

  const [tabValue, setTabValue] = useState(0);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    []
  );
  const [reasonMap, setReasonMap] = useState<Record<number, string>>({});
  const [reportOpen, setReportOpen] = useState(false);
  const [detailRec, setDetailRec] = useState<BidRecommendation | null>(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);

  const filteredRecs = useMemo(() => {
    if (!run) return [];
    const tab = ACTION_TABS[tabValue];
    if (tab === 'all') return run.recommendations;
    return run.recommendations.filter((r) => r.action === tab);
  }, [run, tabValue]);

  const handleReview = useCallback(
    async (index: number, status: 'accepted' | 'rejected') => {
      try {
        await reviewRecommendation({
          runId,
          index,
          body: { status, reason: reasonMap[index] || undefined },
        }).unwrap();
        enqueueSnackbar(
          `Recommendation ${status === 'accepted' ? 'accepted' : 'rejected'}`,
          { variant: status === 'accepted' ? 'success' : 'info' }
        );
      } catch {
        enqueueSnackbar('Failed to update review', { variant: 'error' });
      }
    },
    [reviewRecommendation, runId, reasonMap, enqueueSnackbar]
  );

  const handleBulkReview = useCallback(
    async (status: 'accepted' | 'rejected') => {
      const ids = selectionModel as number[];
      if (ids.length === 0) return;
      try {
        await bulkReview({
          runId,
          body: {
            reviews: ids.map((id) => ({
              index: id as number,
              status,
              reason: reasonMap[id as number] || undefined,
            })),
          },
        }).unwrap();
        enqueueSnackbar(`${ids.length} recommendation(s) ${status}`, {
          variant: status === 'accepted' ? 'success' : 'info',
        });
        setSelectionModel([]);
      } catch {
        enqueueSnackbar('Bulk review failed', { variant: 'error' });
      }
    },
    [bulkReview, runId, selectionModel, reasonMap, enqueueSnackbar]
  );

  const handleOpenReport = useCallback(() => {
    if (!reportData) {
      triggerReport(runId);
    }
    setReportOpen(true);
  }, [reportData, triggerReport, runId]);

  const publishableCount = useMemo(() => {
    if (!run) return 0;
    return run.recommendations.filter(
      (r) =>
        r.review_status === 'accepted' &&
        r.action !== 'hold' &&
        !r.published_at &&
        r.estimated_new_bid != null
    ).length;
  }, [run]);

  const handlePublish = useCallback(async () => {
    setPublishDialogOpen(false);
    try {
      const result = await publishRecommendations(runId).unwrap();
      enqueueSnackbar(
        `Published ${result.published_count} bid change(s)${
          result.failed_count > 0 ? ` (${result.failed_count} failed)` : ''
        }`,
        { variant: result.failed_count > 0 ? 'warning' : 'success' }
      );
    } catch {
      enqueueSnackbar('Failed to publish bid changes', { variant: 'error' });
    }
  }, [publishRecommendations, runId, enqueueSnackbar]);

  const columns: GridColDef<BidRecommendation>[] = useMemo(
    () => [
      {
        field: 'platform',
        headerName: 'Platform',
        width: 110,
        renderCell: (params: GridRenderCellParams<BidRecommendation>) => (
          <Chip
            label={params.value === 'google' ? 'Google' : 'Microsoft'}
            size="small"
            variant="outlined"
          />
        ),
      },
      { field: 'campaign_name', headerName: 'Campaign', width: 180 },
      { field: 'ad_group_name', headerName: 'Ad Group', width: 200 },
      { field: 'match_type', headerName: 'Match Type', width: 110 },
      {
        field: 'current_bid',
        headerName: 'Current Bid',
        width: 110,
        type: 'number',
        valueFormatter: ({ value }: { value: number }) =>
          `$${value.toFixed(2)}`,
      },
      {
        field: 'action',
        headerName: 'Action',
        width: 130,
        renderCell: (params: GridRenderCellParams<BidRecommendation>) => (
          <Chip
            label={actionLabels[params.value as Action]}
            color={actionColors[params.value as Action]}
            size="small"
          />
        ),
      },
      {
        field: 'bid_change_pct',
        headerName: 'Change %',
        width: 100,
        type: 'number',
        valueFormatter: ({ value }: { value: number | null }) =>
          value != null ? `${value > 0 ? '+' : ''}${value}%` : '—',
      },
      {
        field: 'estimated_new_bid',
        headerName: 'Est. New Bid',
        width: 110,
        type: 'number',
        valueFormatter: ({ value }: { value: number | null }) =>
          value != null ? `$${value.toFixed(2)}` : '—',
      },
      {
        field: 'confidence',
        headerName: 'Confidence',
        width: 110,
        renderCell: (params: GridRenderCellParams<BidRecommendation>) => (
          <Chip
            label={params.value}
            color={confidenceColors[params.value as Confidence]}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        field: 'risk_flags',
        headerName: 'Risk Flags',
        width: 180,
        renderCell: (params: GridRenderCellParams<BidRecommendation>) => (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {(params.value as string[]).map((f) => (
              <Chip
                key={f}
                label={f.replace(/_/g, ' ')}
                size="small"
                color="warning"
                variant="outlined"
              />
            ))}
          </Box>
        ),
      },
      {
        field: 'review_status',
        headerName: 'Review',
        width: 150,
        renderCell: (params: GridRenderCellParams<BidRecommendation>) => (
          <Box display="flex" gap={0.5} alignItems="center">
            <Chip
              label={params.value}
              color={reviewStatusColors[params.value as ReviewStatus]}
              size="small"
            />
            {params.row.published_at && (
              <Chip
                label="Published"
                size="small"
                color="info"
                variant="outlined"
              />
            )}
          </Box>
        ),
      },
      {
        field: 'review_reason_input',
        headerName: 'Reason',
        width: 180,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<BidRecommendation>) => {
          const rec = params.row;
          if (rec.review_status !== 'pending') {
            return (
              <Typography variant="caption" color="text.secondary">
                {rec.review_reason || '—'}
              </Typography>
            );
          }
          return (
            <TextField
              size="small"
              variant="standard"
              placeholder="Optional reason"
              value={reasonMap[rec.index] || ''}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                setReasonMap((prev) => ({
                  ...prev,
                  [rec.index]: e.target.value,
                }))
              }
              fullWidth
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<BidRecommendation>) => {
          const rec = params.row;
          if (rec.review_status !== 'pending') return null;
          return (
            <Box display="flex" gap={0.5}>
              <Tooltip title="Accept">
                <IconButton
                  size="small"
                  color="success"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReview(rec.index, 'accepted');
                  }}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReview(rec.index, 'rejected');
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [handleReview, reasonMap]
  );

  if (isLoading || !run) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  const progress =
    run.total_recommendations > 0
      ? (run.reviewed_count / run.total_recommendations) * 100
      : 0;

  const selectedCount = (selectionModel as number[]).length;

  return (
    <Box display="flex" flexDirection="column" width="100%" maxWidth="82vw">
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mt={3} mb={1}>
        <IconButton onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">
          Run: {new Date(run.created_at).toLocaleString()}
        </Typography>
        <Box flexGrow={1} />
        {run.report_s3_url && (
          <Button
            variant="outlined"
            startIcon={<ArticleIcon />}
            onClick={handleOpenReport}
          >
            View Report
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={
            isPublishing ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PublishIcon />
            )
          }
          disabled={publishableCount === 0 || isPublishing}
          onClick={() => setPublishDialogOpen(true)}
        >
          Publish Accepted ({publishableCount})
        </Button>
      </Box>

      {/* Progress bar */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
        />
        <Typography variant="body2" color="text.secondary" noWrap>
          {run.reviewed_count}/{run.total_recommendations} reviewed
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={`All (${run.total_recommendations})`} />
          <Tab label={`Increases (${run.increases})`} />
          <Tab label={`Decreases (${run.decreases})`} />
          <Tab label={`Holds (${run.holds})`} />
          <Tab label={`Manual Review (${run.manual_reviews})`} />
        </Tabs>
      </Box>

      {/* Bulk actions toolbar */}
      {selectedCount > 0 && (
        <Toolbar
          variant="dense"
          sx={{
            bgcolor: 'action.selected',
            borderRadius: 1,
            mb: 1,
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {selectedCount} selected
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={() => handleBulkReview('accepted')}
          >
            Accept Selected
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={() => handleBulkReview('rejected')}
          >
            Reject Selected
          </Button>
        </Toolbar>
      )}

      {/* DataGrid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGridPremium
          rows={filteredRecs}
          columns={columns}
          getRowId={(row) => row.index}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          onRowClick={(params: GridRowParams<BidRecommendation>) =>
            setDetailRec(params.row)
          }
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-cell': { py: 1 },
          }}
          pagination
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[10, 25, 50]}
        />
      </Box>

      {/* Report Drawer */}
      <ReportDrawer
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        markdown={reportData?.markdown || null}
        loading={reportLoading}
      />

      {/* Recommendation detail drawer */}
      <Drawer
        anchor="right"
        open={!!detailRec}
        onClose={() => setDetailRec(null)}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
        PaperProps={{ sx: { width: { xs: '100%', md: 600 } } }}
      >
        {detailRec && <RecommendationDetail rec={detailRec} />}
      </Drawer>

      {/* Publish confirmation dialog */}
      <Dialog
        open={publishDialogOpen}
        onClose={() => setPublishDialogOpen(false)}
      >
        <DialogTitle>Publish Bid Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will update the live bids for{' '}
            <strong>{publishableCount}</strong> accepted recommendation(s) on
            their respective ad platforms. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePublish}
            variant="contained"
            color="primary"
            autoFocus
          >
            Publish
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RunDetailView;
